import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SR_LINKED_STATUSES } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { SortableList } from '../../components/SortableList';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { BASE, CompanyArray, keys, statusTone, useBuildingRole } from './api';
import type { SrBuildingCompany } from './api';

const isLinked = (c: SrBuildingCompany) => (SR_LINKED_STATUSES as readonly string[]).includes(c.status);
const label = (c: SrBuildingCompany) => c.displayName ?? t('srb.companies.unnamed');

/** Companies on the shared tablet: their order on the company picker, and their status. Only administrators change the order. */
export function CompaniesPage() {
  const role = useBuildingRole();
  const canOrder = role === 'building_admin';
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: keys.companies, queryFn: () => api.get(`${BASE}/companies`, { schema: CompanyArray }) });
  const [items, setItems] = useState<SrBuildingCompany[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!q.data) return;
    setItems(q.data.filter(isLinked).sort((a, b) => a.sort - b.sort));
    setDirty(false);
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api.put(`${BASE}/companies/order`, { body: { membershipIds: items.map((c) => c.membershipId) } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.companies });
      toast(t('srb.companies.saved'));
    },
  });
  // The tablet lost a company's link: the next time it asks for its links it is given a new one.
  const reissue = useMutation({
    mutationFn: (id: string) => api.post(`${BASE}/companies/${id}/reissue-link`),
    onSuccess: () => toast(t('srb.companies.reissue.done')),
  });
  const others = (q.data ?? []).filter((c) => !isLinked(c));
  return (
    <>
      <PageHeader title={t('srb.companies.title')} description={t('srb.companies.desc')} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className="space-y-6">
          <Card
            title={t('srb.companies.list')}
            description={canOrder ? t('srb.companies.orderHint') : t('srb.companies.readOnly')}
            footer={
              canOrder ? (
                <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                  {t('srb.companies.save')}
                </Button>
              ) : null
            }
          >
            <ErrorLine error={save.error} />
            <ErrorLine error={reissue.error} />
            {items.length === 0 ? (
              <EmptyState message={t('srb.companies.empty')} />
            ) : (
              <SortableList
                items={items}
                label={t('srb.companies.list')}
                disabled={!canOrder}
                getKey={(c) => c.membershipId}
                getLabel={label}
                onChange={(next) => {
                  setItems(next);
                  setDirty(true);
                }}
                renderItem={(c) => (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{label(c)}</span>
                    <Badge tone={statusTone(c.status)}>{t(`sr.status.${c.status}`)}</Badge>
                    {!c.available ? <Badge tone="warn">{t('srb.companies.signInPaused')}</Badge> : null}
                    {!c.sharesOnSiteList ? <Badge>{t('srb.companies.notOnPhones')}</Badge> : null}
                    {canOrder && (c.status === 'active' || c.status === 'paused') ? (
                      <Button disabled={reissue.isPending} onClick={() => reissue.mutate(c.membershipId)}>
                        {t('srb.companies.reissue')}
                      </Button>
                    ) : null}
                  </span>
                )}
              />
            )}
          </Card>
          {others.length > 0 ? (
            <Card title={t('srb.companies.others')} description={t('srb.companies.othersDesc')}>
              <ul className="divide-y divide-slate-100">
                {others.map((c) => (
                  <li key={c.membershipId} className="flex flex-wrap items-center gap-2 py-2">
                    <span className="font-medium">{label(c)}</span>
                    <Badge tone={statusTone(c.status)}>{t(`sr.status.${c.status}`)}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      )}
    </>
  );
}
